import React, { useState } from 'react';
import Button from '../button';
import BevSafeText from '../bev-safe-text';
import { cn } from '@fshr/shared-react';
import { useTranslations } from '@fshr/context/i18n-context';
import { useTealium } from '@fshr/context/tealium-context';
import { formatPrice } from '@fshr/shared';

interface TransportCardProps {
	date: string;
	time?: string;
	vendor?: string;
	status: string;
	totalCost?: string;
	vehicleSelected?: string;
	description?: string;
	pickupLocation: string;
	dropoffLocation: string;
	guests?: string;
	notes?: string;
	currencyCode?: string;
	cancellationPolicy?: string;
	luggage?: number;
	confirmationNumber?: string;
	chatEnabled?: boolean;
	title?: string;
}

export const TransportCard: React.FC<TransportCardProps> = ({
	date,
	time,
	vendor,
	status,
	totalCost,
	description,
	vehicleSelected,
	pickupLocation,
	dropoffLocation,
	guests,
	notes,
	currencyCode,
	cancellationPolicy,
	luggage,
	confirmationNumber,
	chatEnabled,
	title,
}) => {
	const [isDetailsOpen, setIsDetailsOpen] = useState(false);
	const t = useTranslations();
	const { trackLink } = useTealium();

	const chatToUpdate = () => {
		trackLink('interaction', {
			interaction_type: 'itinerary overlay',
			interaction_name: 'chat to update',
			interaction_detail: 'itinerary timeline',
		});
		window.dispatchEvent(new Event('webchat.open'));
	};

	const getDisplayStatus = (status: string): string | null => {
		const pendingStatuses = ['Open', 'Pending', 'Waitlist', 'On Hold', 'Requested'];
		const confirmedStatuses = ['Closed', 'Reconfirmed'];

		const normalizedStatus = status.toLowerCase();

		if (pendingStatuses.some(s => s.toLowerCase() === normalizedStatus)) {
			return 'Pending';
		}
		if (confirmedStatuses.some(s => s.toLowerCase() === normalizedStatus)) {
			return 'Confirmed';
		}
		return null;
	};

	return (
		<div
			className="flex items-stretch spacing-inline-xs relative cursor-pointer"
			role="listitem"
			aria-label={`${t(title ?? '')} - ${date}`}
		>
			<div className="flex flex-col h-full">
				{/* Timeline dot and line */}
				<div
					className="absolute left-[3px] top-0 bottom-0 flex flex-col items-center"
					aria-hidden={true}
				>
					{/* Dot */}
					<div className="relative flex h-2 w-2">
						<div className="absolute h-2 w-2 rounded-full bg-[rgb(77,77,77,0.45)]" />
					</div>

					{/* Line */}
					<div className="w-[1px] h-full bg-[rgb(77,77,77,0.45)]" />
				</div>
			</div>

			{/* Content */}
			<div className="flex-1 pl-100 pb-200">
				{/* Date */}
				<div className="text-label text-text-secondary mb-080">
					{date} {time && `- ${time}`}
				</div>

				{/* Card */}
				<div
					className="rounded bg-surface-raised-subtle p-200 border border-border-subtle cursor-pointer"
					aria-expanded={isDetailsOpen}
				>
					{/* Title and dropdown icon */}
					<div className="flex justify-between flex-col gap-080 ">
						<div
							className="flex justify-between items-center"
							onClick={() => setIsDetailsOpen(!isDetailsOpen)}
						>
							<h3 className="text-subtitle2 text-text-primary">{t(title ?? '')}</h3>
							<button
								className="w-6 h-6 flex items-center justify-center"
								aria-label={t(isDetailsOpen ? 'Collapse details' : 'Expand details')}
							>
								<div
									aria-hidden={true}
									className={cn([
										'pointer-events-none',
										"content=['']",
										'border-[6px]',
										'border-l-transparent',
										'border-r-transparent',
										'border-b-0',
										'border-t-black',
										'top-1/2',
										'-t-y-1/2',
										'ltr:right-[16px]',
										'rtl:left-0',
										isDetailsOpen ? 'rotate-180' : '',
									])}
								/>
							</button>
						</div>

						{/* Vendor */}
						<div className="text-caption">{vendor}</div>

						{/* Status */}
						{getDisplayStatus(status) && (
							<div
								className={`text-label uppercase ${isDetailsOpen ? 'pb-150' : ''} ${
									getDisplayStatus(status) === 'Pending' ? 'text-[#C57907]' : 'text-[#218242]'
								}`}
								data-status={status.toLowerCase()}
								role="status"
							>
								{t(getDisplayStatus(status)!)}
							</div>
						)}
					</div>

					{isDetailsOpen && (
						<div role="region" aria-label={t(`${title ?? ''} Details`)}>
							{/* Details Grid */}
							<div className="grid grid-cols-2 gap-150 py-150 border-t border-border-subtle">
								<div>
									<h4 className="text-label text-text-primary">{t('Selected date')}</h4>
									<p className="text-body3 text-text-primary">{date}</p>
								</div>
								{time && (
									<div>
										<h5 className="text-label text-text-primary">{t('Time')}</h5>
										<p className="text-body3 text-text-primary">{time}</p>
									</div>
								)}
								{totalCost && (
									<div>
										<h5 className="text-label text-text-primary">{t('Total cost')}</h5>
										<p className="text-body3 text-text-primary">
											{formatPrice({ value: totalCost, currencyCode })}
										</p>
									</div>
								)}
								{description && (
									<div>
										<h5 className="text-label text-text-primary">{t('Description')}</h5>
										<p className="text-body3 text-text-primary">
											<BevSafeText>{description}</BevSafeText>
										</p>
									</div>
								)}
								<div>
									<h5 className="text-label text-text-primary">{t('Vehicle selected')}</h5>
									<p className="text-body3 text-text-primary">{vehicleSelected}</p>
								</div>
								<div>
									<h5 className="text-label text-text-primary">{t('Pickup location')}</h5>
									<p className="text-body3 text-text-primary">{pickupLocation}</p>
								</div>
								<div>
									<h5 className="text-label text-text-primary">{t('Drop off location')}</h5>
									<p className="text-body3 text-text-primary">{dropoffLocation}</p>
								</div>
								<div>
									<h5 className="text-label text-text-primary">{t('Number of luggage')}</h5>
									<p className="text-body3 text-text-primary">{luggage ?? 0}</p>
								</div>
								{confirmationNumber && (
									<div>
										<h5 className="text-label text-text-primary">{t('Confirmation number')}</h5>
										<p className="text-body3 text-text-primary">{confirmationNumber}</p>
									</div>
								)}
								{guests && (
									<div>
										<h5 className="text-label text-text-primary">{t('Guests')}</h5>
										<p className="text-body3 text-text-primary">{t('{0} adults', [guests])}</p>
									</div>
								)}
							</div>

							{/* Notes */}
							{notes && (
								<div className="mt-150">
									<h4 className="text-label text-text-primary">{t('Notes')}</h4>
									<div
										className="text-body3 text-text-primary"
										dangerouslySetInnerHTML={{ __html: notes }}
										role="note"
									/>
								</div>
							)}
							{/* Description */}
							{description && description !== 'null' && (
								<div className="mt-150">
									<h5 className="text-label text-text-primary">{t('Description')}</h5>
									<div
										className="text-body3 text-text-primary"
										dangerouslySetInnerHTML={{ __html: description || '' }}
										role="note"
									/>
								</div>
							)}

							{/* Cancellation Policy */}
							{cancellationPolicy && (
								<div className="mt-150">
									<h4 className="text-label text-text-primary">{t('Cancellation policy')}</h4>
									<div
										className="text-body3 text-text-primary"
										dangerouslySetInnerHTML={{ __html: t(cancellationPolicy) }}
										role="note"
									/>
								</div>
							)}

							{/* Chat Button */}
							{chatEnabled && (
								<Button
									intent="outline"
									size="small"
									className="mt-150"
									aria-label={t('Chat to update')}
									onClick={chatToUpdate}
								>
									{t('Chat to update')}
								</Button>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
